"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import {
  HeroSearchSection,
  MarketplaceStatsCards,
  CategoryCard,
  CategoryScroller,
  VehicleGrid,
  FeaturedVehiclesGrid,
  StatsSkeleton,
  mapDashboardMetricsToMarketplaceStats,
  mapSummaryToMarketplaceStats,
} from "@/app/common/components/buysell";
import { userProductRoutes } from "@/lib/userProductRoutes";
import { PRODUCT_THEME as T, GRADIENT, PRIMARY_DARK } from "@/lib/theme";
import { toBuySellListPayload } from "@/lib/buySellListUtils";
import {
  getBuySellDashboardStats,
  getBuySellFeaturedVehicles,
  getBuySellListPage,
  getBuySellRecentVehicles,
  type BuySellProduct,
  type BuySellDashboardStatsResponse,
} from "@/model/services/buysellapi";
import { getCategories, type Category } from "@/model/services/category";
import { getMarketplaceDashboardSummary, type MarketplaceDashboardSummary } from "@/model/services/dashboard";
import { useNotification } from "@/hooks/useNotification";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { ensureLoggedInToViewProduct } from "@/lib/requireMarketplaceLogin";
import type { MarketplaceStats } from "@/app/common/components/buysell/utils";
import { EMPTY_FILTERS } from "@/app/admin/portal/buysell/_components/interface/buysell_interface";
import { useMarketplaceAuth } from "@/components/marketplace/MarketplaceAuthProvider";
import { MARKETPLACE } from "@/constants/marketplace";
import { toErrorMessage } from "@/lib/errors";
import { isAbortError } from "@/lib/apiCache";
import { GoogleAdBanner } from "@/components/ads/GoogleAdBanner";
import { SHOW_ADS } from "@/components/ads/adsConfig";
import { SearchableSelect, type SelectOption } from "@/components/common/SearchableSelect";

const EMPTY_STATS: MarketplaceStats = {
  totalListings: 0,
  activeListings: 0,
  soldVehicles: 0,
  totalOffers: 0,
};

export default function UserProductDashboard() {
  const router = useRouter();
  const { notify } = useNotification();
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const { isLoggedIn, authReady } = useMarketplaceAuth();

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [featuredVehicles, setFeaturedVehicles] = useState<BuySellProduct[]>([]);
  const [recentVehicles, setRecentVehicles] = useState<BuySellProduct[]>([]);
  const [dashboardData, setDashboardData] = useState<
    BuySellDashboardStatsResponse["data"] | null
  >(null);
  const [summaryData, setSummaryData] = useState<MarketplaceDashboardSummary | null>(
    null,
  );
  const [statsError, setStatsError] = useState("");
  const [statsUpdatedAt, setStatsUpdatedAt] = useState<Date | null>(null);
  const [featuredError, setFeaturedError] = useState("");
  const [recentVehiclesError, setRecentVehiclesError] = useState("");
  const [statsLoading, setStatsLoading] = useState(true);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [recentVehiclesLoading, setRecentVehiclesLoading] = useState(true);
  const [exploreSort, setExploreSort] = useState("newest");

  const loadExplorePage = useCallback(
    async (page: number, signal: AbortSignal) => {
      const result = await getBuySellListPage(
        {
          ...toBuySellListPayload({
            ...EMPTY_FILTERS,
            usear_type: isLoggedIn ? "buy" : "all",
          }),
          page,
          limit: MARKETPLACE.VEHICLE_PAGE_SIZE,
        },
        { signal },
      );
      return {
        items: result.items ?? [],
        total: result.total ?? 0,
        totalPages: result.totalPages ?? 1,
        page: result.page ?? page,
      };
    },
    [isLoggedIn],
  );

  const exploreList = useInfiniteScroll(loadExplorePage);

  const pathname = usePathname();
  const exploreResyncRef = useRef(false);

  useEffect(() => {
    if (exploreResyncRef.current) {
      exploreList.reset();
    } else {
      exploreResyncRef.current = true;
    }
  }, [pathname, exploreList.reset]);

  const scrolledForHashRef = useRef(false);
  const exploreStateRef = useRef(exploreList);
  const tryScrollToExploreRef = useRef<() => void>(() => {});

  const tryScrollToExplore = useCallback(() => {
    if (scrolledForHashRef.current) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#explore-all-vehicles") return;
    const el = document.getElementById("explore-all-vehicles");
    if (!el) {
      window.setTimeout(() => tryScrollToExploreRef.current(), 100);
      return;
    }
    const { loading, items } = exploreStateRef.current;
    if (loading && items.length === 0) {
      window.setTimeout(() => tryScrollToExploreRef.current(), 100);
      return;
    }
    el.scrollIntoView({ behavior: "smooth" });
    scrolledForHashRef.current = true;
  }, []);

  useEffect(() => {
    exploreStateRef.current = exploreList;
    tryScrollToExploreRef.current = tryScrollToExplore;
  }, [exploreList, tryScrollToExplore]);

  useEffect(() => {
    tryScrollToExplore();
  }, [tryScrollToExplore, exploreList.loading, exploreList.items.length]);

  useEffect(() => {
    const onHashChange = () => tryScrollToExplore();
    window.addEventListener("hashchange", onHashChange);
    return () =>
      window.removeEventListener("hashchange", onHashChange);
  }, [tryScrollToExplore]);

  useEffect(() => {
    if (
      exploreList.error &&
      exploreList.items.length === 0 &&
      !isAbortError(exploreList.error)
    ) {
      notifyRef.current({
        type: "error",
        message: exploreList.error.message,
      });
    }
  }, [exploreList.error, exploreList.items.length]);

  /** Critical path first (explore), then stats/featured/categories — don't block first paint. */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    setStatsLoading(true);
    setRecentVehiclesLoading(true);
    setStatsError("");
    setRecentVehiclesError("");

    void getCategories({ activeOnly: true })
      .then((cats) => {
        if (!signal.aborted) setCategories(cats ?? []);
      })
      .catch(() => {
        /* categories are non-critical for first paint */
      });

    const statsJobs: Promise<void>[] = [
      getBuySellDashboardStats()
        .then((dashStats) => {
          if (signal.aborted) return;
          setDashboardData(dashStats);
          if (dashStats) setStatsUpdatedAt(new Date());
        })
        .catch((err) => {
          if (signal.aborted) return;
          setStatsError(toErrorMessage(err, "Failed to load statistics"));
        }),
      getMarketplaceDashboardSummary("last_30_days")
        .then((summary) => {
          if (signal.aborted) return;
          setSummaryData(summary);
          if (summary) setStatsUpdatedAt(new Date());
        })
        .catch((err) => {
          if (signal.aborted) return;
          setStatsError((prev) => prev || toErrorMessage(err, "Failed to load statistics"));
        }),
    ];

    void Promise.allSettled(statsJobs).finally(() => {
      if (!signal.aborted) setStatsLoading(false);
    });

    void getBuySellRecentVehicles(MARKETPLACE.RECENT_SECTION_LIMIT)
      .then((data) => {
        if (signal.aborted) return;
        const items = data ?? [];
        setRecentVehicles(items);
      })
      .catch((err) => {
        if (signal.aborted) return;
        setRecentVehiclesError(toErrorMessage(err, "Failed to load recent vehicles"));
      })
      .finally(() => {
        if (!signal.aborted) setRecentVehiclesLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!authReady) return;

    let cancelled = false;
    setFeaturedLoading(true);
    setFeaturedError("");

    void getBuySellFeaturedVehicles(MARKETPLACE.FEATURED_SECTION_LIMIT)
      .then((data) => {
        if (cancelled) return;
        const items = data ?? [];
        setFeaturedVehicles(items);
      })
      .catch((err) => {
        if (cancelled) return;
        setFeaturedError(toErrorMessage(err, "Failed to load featured vehicles"));
      })
      .finally(() => {
        if (!cancelled) setFeaturedLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authReady]);

  const loadFeaturedVehicles = useCallback(() => {
    setFeaturedLoading(true);
    setFeaturedError("");
    void getBuySellFeaturedVehicles(MARKETPLACE.FEATURED_SECTION_LIMIT)
      .then((data) => setFeaturedVehicles(data ?? []))
      .catch((err) =>
        setFeaturedError(toErrorMessage(err, "Failed to load featured vehicles")),
      )
      .finally(() => setFeaturedLoading(false));
  }, [isLoggedIn]);

  const marketplaceStats = useMemo(() => {
    const offers = dashboardData?.marketplace?.totalOffers ?? 0;
    if (dashboardData?.marketplace) {
      const metrics = mapDashboardMetricsToMarketplaceStats(dashboardData.marketplace);
      if (!summaryData) return metrics;

      return {
        ...metrics,
        totalUsers: summaryData.totalUsers ?? 0,
        newListingsInPeriod: summaryData.periodCounts?.totalProducts ?? 0,
        newUsersInPeriod: summaryData.periodCounts?.totalUsers ?? 0,
      };
    }
    if (summaryData) {
      return mapSummaryToMarketplaceStats(summaryData, offers);
    }
    return EMPTY_STATS;
  }, [dashboardData, summaryData]);

  const mySellStats = useMemo(() => {
    if (dashboardData?.mySell) {
      return mapDashboardMetricsToMarketplaceStats(dashboardData.mySell);
    }
    return null;
  }, [dashboardData]);

  const exploreSortOptions: SelectOption[] = [
    
    { value: "price_asc", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
  ];

  const sortedExploreItems = useMemo(() => {
    const items = exploreList.items ?? [];
    if (exploreSort === "newest") {
      return [...items].sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime(),
      );
    }
    if (exploreSort === "oldest") {
      return [...items].sort(
        (a, b) =>
          new Date(a.createdAt ?? 0).getTime() -
          new Date(b.createdAt ?? 0).getTime(),
      );
    }
    if (exploreSort === "price_asc") {
      return [...items].sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    }
    if (exploreSort === "price_desc") {
      return [...items].sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    }
    return items;
  }, [exploreList.items, exploreSort]);

  const handleSearch = () => {
    router.push(
      userProductRoutes.list({
        ...(search.trim() ? { q: search.trim() } : {}),
        // ...(categoryId ? { category_id: categoryId } : {}),
      }),
    );
  };

  const handleViewProduct = useCallback(
    async (productId: string) => {
      const allowed = await ensureLoggedInToViewProduct(productId, {
        notify,
        isLoggedIn,
        authReady,
        onNeedLogin: (loginPath) => router.push(loginPath),
      });
      if (allowed) router.push(userProductRoutes.view(productId));
    },
    [notify, router, isLoggedIn, authReady],
  );

  return (
    <Box>
      <HeroSearchSection
        search={search}
        categoryId={categoryId}
        categories={categories}
        onSearchChange={setSearch}
        onCategoryChange={setCategoryId}
        onSearch={handleSearch}
      />

      {SHOW_ADS && <GoogleAdBanner placement="dashboard" format="auto" responsive />}

      <Box sx={{ mt: 4 }}>
        {statsError && !statsLoading ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {statsError}
          </Alert>
        ) : null}
        {statsLoading ? (
          <StatsSkeleton />
        ) : (
          <MarketplaceStatsCards
            stats={marketplaceStats}
            mySell={mySellStats}
            updatedAt={statsUpdatedAt}
            onViewMyListings={() => router.push(userProductRoutes.sellVehicle())}
          />
        )}
      </Box>

      <Box sx={{ mt: 5 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 24,
            mb: 3,
            color: T.color.textPrimary,
            letterSpacing: "-0.01em",
          }}
        >
          Top Categories
        </Typography>
        <CategoryScroller>
          {categories.map((cat) => (
            <CategoryCard
              key={cat._id}
              category={cat}
              onClick={(id) =>
                router.push(userProductRoutes.list({ category_id: id }))
              }
            />
          ))}
        </CategoryScroller>
      </Box>

      <Box sx={{ mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 24,
              color: T.color.textPrimary,
              letterSpacing: "-0.01em",
            }}
          >
            Featured Vehicles
          </Typography>
          <Button
            onClick={() => router.push(userProductRoutes.featuredVehicles())}
            sx={{ textTransform: "none", fontWeight: 700, color: "primary.main" }}
          >
            View all →
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Premium listings with paid featured visibility on TRUCKS99.
        </Typography>
        {authReady ? (
          <FeaturedVehiclesGrid
            products={featuredVehicles}
            loading={featuredLoading}
            error={featuredError}
            onRetry={loadFeaturedVehicles}
            onViewDetails={(id) => void handleViewProduct(id)}
            onBrowseAll={() => router.push(userProductRoutes.list())}
            emptyDescription="No featured listings yet. Browse all vehicles or feature yours from your listing page."
          />
        ) : (
          <FeaturedVehiclesGrid products={[]} loading onViewDetails={() => {}} />
        )}
      </Box>
      <Box sx={{ mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 24,
              color: T.color.textPrimary,
              letterSpacing: "-0.01em",
            }}
          >
            Recently Added Vehicles
          </Typography>
          <Button
            onClick={() => router.push(userProductRoutes.list())}
            sx={{ textTransform: "none", fontWeight: 700, color: "primary.main" }}
          >
            View all →
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fresh listings posted recently on TRUCKS99.
        </Typography>
        {recentVehiclesError && !recentVehiclesLoading ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {recentVehiclesError}
          </Alert>
        ) : null}
        <VehicleGrid
          products={recentVehicles}
          loading={recentVehiclesLoading}
          onProductClick={(id) => void handleViewProduct(id)}
          emptyDescription="No recent vehicles yet. Check back soon for newly listed trucks."
        />
      </Box>
      <Box
        id="explore-all-vehicles"
        sx={{ mt: 5, scrollMarginTop: "88px" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 24,
              color: T.color.textPrimary,
              letterSpacing: "-0.01em",
            }}
          >
            Explore All Vehicles
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "nowrap" }}>
            <SearchableSelect
              value={exploreSort}
              onChange={setExploreSort}
              options={exploreSortOptions}
              aria-label="Sort Explore All Vehicles"
              sx={{ minWidth: { xs: 140, sm: 180 } }}
            />
            <Button
              onClick={() => router.push(userProductRoutes.list())}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "primary.main",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              View all →
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Browse every active listing on TRUCKS99
          {!exploreList.loading && exploreList.items.length > 0
            ? ` · ${exploreList.total.toLocaleString("en-IN")} vehicle${exploreList.total !== 1 ? "s" : ""}`
            : ""}
          .
        </Typography>
        {exploreList.error &&
        exploreList.items.length === 0 &&
        !isAbortError(exploreList.error) ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {exploreList.error.message ||
              "No vehicles to show yet. List a vehicle or check back soon."}
          </Alert>
        ) : null}
        <VehicleGrid
          products={sortedExploreItems}
          loading={exploreList.loading}
          hasMore={exploreList.hasMore}
          sentinelRef={exploreList.sentinelRef}
          onProductClick={(id) => void handleViewProduct(id)}
          emptyDescription="No vehicles to explore yet. Be the first to list on TRUCKS99."
        />
      </Box>


    </Box>
  );
}
